import React, { useState, useEffect } from 'react';
import Modal from '../ui/Modal';
import Icon from '../ui/Icon';
//import { base44 } from '@/api/base44Client';

const agentStatuses = [
  { value: 'pending', label: 'Pending Approval' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' }
];

const specializationOptions = [
  'Residential', 'Commercial', 'Luxury', 'First-Time Buyers',
  'Investment Properties', 'Condos', 'Land', 'New Construction'
];

export default function AgentForm({ open, onClose, agent, onSave }) {
  const [formData, setFormData] = useState({
    full_name: '', email: '', phone: '', license_number: '', photo_url: '',
    status: 'pending', hire_date: new Date().toISOString().split('T')[0],
    commission_rate: '', bio: '', specializations: []
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (agent) {
      setFormData({
        ...agent,
        commission_rate: agent.commission_rate || '',
        specializations: agent.specializations || []
      });
    } else if (open) {
      setFormData({
        full_name: '', email: '', phone: '', license_number: '', photo_url: '',
        status: 'pending', hire_date: new Date().toISOString().split('T')[0],
        commission_rate: '', bio: '', specializations: []
      });
    }
  }, [agent, open]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleSpecialization = (spec) => {
    setFormData(prev => ({
      ...prev,
      specializations: prev.specializations.includes(spec)
        ? prev.specializations.filter(s => s !== spec)
        : [...prev.specializations, spec]
    }));
  };
const handlePhotoUpload = async (e) => {
  const file = e.target.files[0];
  if (!file) return;

  setUploading(true);

  // Simulate a "file upload" by creating a temporary local URL
  // In a real app, this is where you'd call your API
  const fakeUpload = new Promise((resolve) => {
    setTimeout(() => {
      resolve({ file_url: URL.createObjectURL(file) });
    }, 500); // simulate 0.5s upload
  });

  const result = await fakeUpload;

  setFormData(prev => ({
    ...prev,
    photo_url: result.file_url
  }));

  setUploading(false);
};

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const dataToSave = {
      ...formData,
      commission_rate: formData.commission_rate ? Number(formData.commission_rate) : null
    };
    await onSave(dataToSave);
    setSaving(false);
  };

  return (
    <Modal open={open} onClose={onClose} title={agent ? 'Edit Agent' : 'Onboard New Agent'}>
      <form onSubmit={handleSubmit}>
        <div className="flex items-center gap-6 mb-6">
          <div style={{ position: 'relative' }}>
            <div style={{ width: '6rem', height: '6rem', borderRadius: '0.75rem', background: 'var(--slate-100)', overflow: 'hidden' }}>
              {formData.photo_url ? (
                <img src={formData.photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--slate-400)' }}>
                  {uploading ? <div className="spinner" /> : 'Photo'}
                </div>
              )}
            </div>
            <input type="file" accept="image/*" onChange={handlePhotoUpload} style={{ display: 'none' }} id="agent-photo" />
            <label htmlFor="agent-photo" style={{ position: 'absolute', bottom: '-0.5rem', right: '-0.5rem', padding: '0.5rem', background: 'var(--slate-900)', borderRadius: '0.5rem', cursor: 'pointer' }}>
              <Icon name="upload" size={16} style={{ color: 'white' }} />
            </label>
          </div>
          <div className="flex-1">
            <div className="mb-4">
              <label className="label">Full Name *</label>
              <input className="input" value={formData.full_name} onChange={(e) => handleChange('full_name', e.target.value)} placeholder="John Smith" required />
            </div>
            <div>
              <label className="label">Email *</label>
              <input className="input" type="email" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} placeholder="john@example.com" required />
            </div>
          </div>
        </div>
        
        <div className="grid grid-2 gap-4">
          <div>
            <label className="label">Phone *</label>
            <input className="input" value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)} placeholder="(555) 123-4567" required />
          </div>
          
          <div>
            <label className="label">License Number *</label>
            <input className="input" value={formData.license_number} onChange={(e) => handleChange('license_number', e.target.value)} placeholder="RE12345678" required />
          </div>
          
          <div>
            <label className="label">Status</label>
            <select className="input select" value={formData.status} onChange={(e) => handleChange('status', e.target.value)}>
              {agentStatuses.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          
          <div>
            <label className="label">Hire Date</label>
            <input className="input" type="date" value={formData.hire_date} onChange={(e) => handleChange('hire_date', e.target.value)} />
          </div>
          
          <div style={{ gridColumn: 'span 2' }}>
            <label className="label">Commission Rate (%)</label>
            <input className="input" type="number" step="0.5" min="0" max="100" value={formData.commission_rate} onChange={(e) => handleChange('commission_rate', e.target.value)} placeholder="3.0" />
          </div>
          
          <div style={{ gridColumn: 'span 2' }}>
            <label className="label">Bio</label>
            <textarea className="input textarea" value={formData.bio} onChange={(e) => handleChange('bio', e.target.value)} rows={3} placeholder="Brief biography..." />
          </div>
          
          <div style={{ gridColumn: 'span 2' }}>
            <label className="label">Specializations</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {specializationOptions.map(spec => (
                <span
                  key={spec}
                  className={`badge cursor-pointer transition ${formData.specializations.includes(spec) ? 'badge-solid-emerald' : 'badge-slate'}`}
                  onClick={() => toggleSpecialization(spec)}
                  style={{ cursor: 'pointer' }}
                >
                  {spec}
                  {formData.specializations.includes(spec) && (
                    <Icon name="x" size={12} style={{ marginLeft: '0.25rem' }} />
                  )}
                </span>
              ))}
            </div>
          </div>
        </div>
        
        <div className="modal-footer" style={{ marginTop: '1.5rem' }}>
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving && <div className="spinner" style={{ borderTopColor: 'white' }} />}
            {agent ? 'Update Agent' : 'Onboard Agent'}
          </button>
        </div>
      </form>
    </Modal>
  );
}